import { useEffect, useRef, useState } from 'react';

export interface PickerOption {
  id: string;
  label: string;
}

interface PickerProps {
  label: string;
  /** What the closed picker reads, which is the current pick or why there is none. */
  value: string;
  options: PickerOption[];
  selectedId: string;
  onSelect: (id: string) => void;
  /** Run when the list opens, for work that is only worth doing on demand. */
  onOpen?: () => void;
  disabled?: boolean;
}

/**
 * One field of the status line that can be clicked to change what it reports.
 * The list opens upward, since the line sits along the bottom of the screen.
 */
export function Picker({
  label,
  value,
  options,
  selectedId,
  onSelect,
  onOpen,
  disabled = false,
}: PickerProps) {
  const [open, setOpen] = useState(false);
  const field = useRef<HTMLDivElement>(null);

  // A press anywhere else puts the list away, including one that lands on the
  // instrument behind it.
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: PointerEvent) => {
      if (!field.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const toggle = () => {
    if (disabled) return;
    if (!open) onOpen?.();
    setOpen(!open);
  };

  return (
    <div className="field" ref={field}>
      <dt>{label}</dt>
      <dd>
        <button
          type="button"
          className="pick"
          aria-haspopup="listbox"
          aria-expanded={open}
          disabled={disabled || options.length === 0}
          onClick={toggle}
        >
          {value}
        </button>

        {open && (
          <ul className="menu" role="listbox">
            {options.map((option) => (
              <li key={option.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={option.id === selectedId}
                  className={option.id === selectedId ? 'chosen' : undefined}
                  onClick={() => {
                    onSelect(option.id);
                    setOpen(false);
                  }}
                >
                  {option.label}
                </button>
              </li>
            ))}
          </ul>
        )}
      </dd>
    </div>
  );
}
